/**
 * RSVP Page
 * 
 * Allows users to RSVP for upcoming events for their associated riders
 * - Shows upcoming events (not yet started)
 * - Displays RSVP status for each rider
 * - Allows toggling RSVP status: Coming (green check) → Maybe (orange question) → Not Coming (red X) → Unknown (gray question)
 * 
 * Requirements: 3.1, 3.2, 3.3
 */

import { useState, useEffect, useCallback } from 'react';
import { restClient, Event as ApiEvent, Rider, EventType } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../auth/AuthProvider';
import { parseError } from '../utils/errorHandling';
import { formatRiderName } from '../utils/riderDisplay';
import { parseExpectedAttendance, getRSVPStatus as getUtilRSVPStatus, updateRSVPStatus, RSVPDisplayStatus } from '../utils/rsvpUtils';
import { isEventVisibleToUser, filterRidersByEvent } from '../utils/eventFiltering';
import { useGroupLeaderStore } from '../store/groupLeaderStore';

interface RiderWithRole extends Rider {
  role?: string;
}

export function RSVP() {
  const { user } = useAuth();
  const isGroupLeader = useGroupLeaderStore(s => s.isGroupLeader);
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [riders, setRiders] = useState<RiderWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);

  /**
   * Fetch event types for color mapping
   */
  const fetchEventTypes = useCallback(async () => {
    try {
      const types = await restClient.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      // Silent error handling - will use default colors
    }
  }, []);

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = useCallback((eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  }, [eventTypes]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [eventsData, ridersData] = await Promise.all([
        restClient.getEvents(),
        restClient.getRiders(),
      ]);

      const now = new Date();
      const upcomingEvents = eventsData
        .filter(event => {
          const eventStart = new Date(event.event_starttime);
          return eventStart > now && event.event_status !== 'canceled';
        })
        .sort((a, b) => new Date(a.event_starttime).getTime() - new Date(b.event_starttime).getTime());

      const associatedRiders = ridersData.filter(rider => {
        // Skip disabled riders
        if (rider.disabled === '1' || rider.disabled === 1) {
          return false;
        }
        
        // Get current season
        const currentSeason = new Date().getFullYear().toString();
        
        // Check if rider is in current season
        // Either primary season matches OR seasons_active contains current season
        const isInCurrentSeason = rider.season === currentSeason || 
          (rider.seasons_active && rider.seasons_active.includes(currentSeason));
        
        if (!isInCurrentSeason) {
          return false;
        }
        
        if (!rider.associatedUsers) return false;
        
        try {
          const users = JSON.parse(rider.associatedUsers);
          return users.some((u: any) => 
            u.user_id === user?.username || 
            u.user_id === user?.email ||
            u.email === user?.email ||
            u.email === user?.username
          );
        } catch {
          return false;
        }
      });

      // Filter events to only those visible to the user's associated riders
      const associatedRiderIds = associatedRiders.map(r => r.rider_id);
      const visibleEvents = upcomingEvents.filter(event =>
        isEventVisibleToUser(event, associatedRiderIds)
      );

      setEvents(visibleEvents);
      setRiders(associatedRiders);
    } catch (err) {
      const parsed = parseError(err);
      setError(parsed.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
    fetchEventTypes();
  }, [loadData, fetchEventTypes]);

  const getRSVPStatusForRider = useCallback((event: ApiEvent, riderId: string): RSVPDisplayStatus => {
    const attendance = parseExpectedAttendance(event.event_expectedAttendance);
    return getUtilRSVPStatus(attendance, riderId);
  }, []);

  const handleUpdateRSVP = useCallback(async (event: ApiEvent, riderId: string, currentStatus: RSVPDisplayStatus) => {
    const key = `${event.event_id}-${riderId}`;
    setUpdating(prev => ({ ...prev, [key]: true }));

    try {
      const result = await updateRSVPStatus(event, riderId, currentStatus);

      if (result.success) {
        setEvents(prevEvents =>
          prevEvents.map(e =>
            e.event_id === event.event_id
              ? { ...e, event_expectedAttendance: result.updatedAttendance }
              : e
          )
        );
        // Invalidate dashboard cache so navigating to Dashboard shows fresh RSVP data
        restClient.clearDashboardCache();
      } else {
        alert(`Failed to update RSVP: ${result.error}`);
      }
    } catch (err) {
      const parsed = parseError(err);
      alert(`Failed to update RSVP: ${parsed.message}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const getRSVPIcon = useCallback((status: RSVPDisplayStatus, isUpdating: boolean) => {
    if (isUpdating) {
      return <LoadingSpinner size="sm" />;
    }

    switch (status) {
      case 'coming':
        return (
          <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'maybe':
        return (
          <svg className="w-8 h-8 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
      case 'not-coming':
        return (
          <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
    }
  }, []);

  const getRSVPLabel = useCallback((status: RSVPDisplayStatus) => {
    switch (status) {
      case 'coming': return 'Coming';
      case 'maybe': return 'Maybe';
      case 'not-coming': return 'Not Coming';
      default: return 'Click to RSVP';
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const formatTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Event RSVP</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-2 md:px-4 pt-3 pb-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {events.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg text-gray-600">No upcoming events available for RSVP</p>
            </div>
          </Card>
        ) : riders.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg text-gray-600">No associated riders found</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Desktop Table View */}
            <Card className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">Event</th>
                      <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">Date & Time</th>
                      <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">Rider</th>
                      <th className="text-center p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">RSVP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {events.map(event => {
                      // Filter riders to only those invited to this event
                      const eventRiders = filterRidersByEvent(riders, event);
                      return eventRiders.map(rider => {
                        const status = getRSVPStatusForRider(event, rider.rider_id);
                        const key = `${event.event_id}-${rider.rider_id}`;
                        const isUpdating = updating[key] || false;

                        return (
                          <tr key={key} className="hover:bg-gray-50 transition-colors">
                            <td className="p-4">
                              <div className="mb-2">
                                <span 
                                  className="inline-block px-3 py-1 text-sm font-semibold text-white rounded-full"
                                  style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                                >
                                  {event.event_type}
                                </span>
                              </div>
                              <div className="font-semibold text-gray-900">{event.event_name}</div>
                              <div className="text-base text-gray-600">
                                {event.event_location_name || event.event_location}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-gray-900">{formatDate(event.event_starttime)}</div>
                              <div className="text-base text-gray-600">
                                {formatTime(event.event_starttime)} - {formatTime(event.event_endtime)}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="font-medium text-gray-900">{formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}</span>
                            </td>
                            <td className="p-4 text-center">
                              <button
                                onClick={() => handleUpdateRSVP(event, rider.rider_id, status)}
                                disabled={isUpdating}
                                className="inline-flex flex-col items-center gap-1 p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={getRSVPLabel(status)}
                              >
                                {getRSVPIcon(status, isUpdating)}
                                <span className="text-base font-medium text-gray-600">{getRSVPLabel(status)}</span>
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {events.map(event => {
                // Filter riders to only those invited to this event
                const eventRiders = filterRidersByEvent(riders, event);
                return eventRiders.map(rider => {
                  const status = getRSVPStatusForRider(event, rider.rider_id);
                  const key = `${event.event_id}-${rider.rider_id}`;
                  const isUpdating = updating[key] || false;

                  return (
                    <Card key={key}>
                      <div className="space-y-3">
                        {/* Event Type Badge */}
                        <div>
                          <span 
                            className="inline-block px-3 py-1 text-sm font-semibold text-white rounded-full"
                            style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                          >
                            {event.event_type}
                          </span>
                        </div>
                        
                        {/* Event Info */}
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{event.event_name}</h3>
                          <p className="text-base text-gray-600">
                            {event.event_location_name || event.event_location}
                          </p>
                        </div>

                        {/* Date & Time */}
                        <div className="text-base">
                          <p className="text-gray-900 font-medium">{formatDate(event.event_starttime)}</p>
                          <p className="text-gray-600">
                            {formatTime(event.event_starttime)} - {formatTime(event.event_endtime)}
                          </p>
                        </div>

                        {/* Rider */}
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-base text-gray-600">Rider</p>
                          <p className="font-medium text-gray-900">{formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}</p>
                        </div>

                        {/* RSVP Button */}
                        <div className="pt-2 border-t border-gray-200">
                          <button
                            onClick={() => handleUpdateRSVP(event, rider.rider_id, status)}
                            disabled={isUpdating}
                            className="w-full flex items-center justify-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={getRSVPLabel(status)}
                          >
                            {getRSVPIcon(status, isUpdating)}
                            <span className="text-base font-medium text-gray-700">{getRSVPLabel(status)}</span>
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                });
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RSVP;
